/**
 * Type-only definitions for the BrandContext wire shape.
 *
 * Lives in its own module (no `'server-only'` / `'use server'` directives)
 * so both server-side code (`lib/workspace.ts`) and client-side code
 * (settings forms, the active-scope provider) can `import type { ... }`
 * without runtime coupling.
 *
 * Wire-format mirror of the Pydantic models in
 * `apps/api/schemas/workspace.py`. v3 added onboarding-wizard fields
 * (role / accountType / industry / monthlySpend / platforms); the schema
 * accepts both v2 and v3 reads but writes default to v3. `brandWebsite`
 * was originally
 * a v3 BrandContext field but has since been promoted to a top-level
 * column on `Brand` (read off `BrandResponse.brandWebsite` instead).
 */

export type CompetitorEntry = {
  name: string
  url: string
  whereWeCompete: string
  whyWeWin: string
}

export type BrandContext = {
  schemaVersion: 2 | 3
  brandDescription: string
  brandVoices: string[]
  sellingPoints: string[]
  targetAudiences: string[]
  competitors: CompetitorEntry[]
  additionalInfo: string
  // Extra reference URLs (pricing / blog / help center / sub-brands).
  // Injected into BRAND.md's `## Other URLs` section; NOT a crawl source.
  otherUrls: string[]
  // v3 onboarding-wizard fields. Optional on the wire (default empty),
  // present on the type so wizard + settings can read/write uniformly.
  role: string
  accountType: string
  industry: string
  monthlySpend: string
  platforms: string[]
  // Biggest paid-media pain (onboarding wizard, first brand). PMF signal.
  pain: string
  // Language the brand profile was (re)generated in ('en' | 'zh' | 'ar'). Empty
  // for legacy rows written before language-awareness; readers fall back to
  // the UI locale.
  language: string
}

export const EMPTY_BRAND_CONTEXT: BrandContext = {
  schemaVersion: 3,
  brandDescription: '',
  brandVoices: [],
  sellingPoints: [],
  targetAudiences: [],
  competitors: [],
  additionalInfo: '',
  otherUrls: [],
  role: '',
  accountType: '',
  industry: '',
  monthlySpend: '',
  platforms: [],
  pain: '',
  language: '',
}
